import { useState, useEffect, useCallback, useRef } from 'react';
import { Newspaper, ExternalLink, RefreshCw, Clock } from 'lucide-react';

export default function NewsWidget() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [sources, setSources] = useState([]);
  
  // Use ref to access current news value in callback (avoid stale closure)
  const newsRef = useRef(news);
  useEffect(() => { newsRef.current = news; }, [news]);

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/news/local`
      );
      
      if (!response.ok) throw new Error('News fetch failed');
      
      const data = await response.json();
      setNews(data.articles || []);
      setLastUpdated(data.last_updated);
      setSources(data.sources || []);
    } catch (err) {
      // Remove console.log in production
      // Keep existing news if refresh fails (use ref to avoid stale closure)
      if (newsRef.current.length === 0) {
        setNews([
          {
            title: 'Aktuelle Nachrichten werden geladen...',
            source: 'System',
            date: new Date().toLocaleDateString('de-CH'),
            url: '#'
          }
        ]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNews();
    // Refresh every 5 minutes for real-time updates
    const interval = setInterval(fetchNews, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNews]);

  return (
    <div className="border border-border rounded-lg p-4 bg-card" data-testid="news-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg flex items-center gap-2">
          <Newspaper className="h-5 w-5 text-orange-500" />
          LOKALE NACHRICHTEN
        </h3>
        <button 
          onClick={fetchNews}
          disabled={loading}
          className="p-1.5 hover:bg-muted rounded transition-colors disabled:opacity-50"
          title="Aktualisieren"
        >
          <RefreshCw className={`h-4 w-4 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      
      {loading && news.length === 0 ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-1"></div>
              <div className="h-3 bg-muted rounded w-1/4"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
          {news.slice(0, 8).map((item, i) => (
            <a 
              key={i}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block group hover:bg-muted/50 rounded p-2 -mx-2 transition-colors border-b border-border/50 last:border-0"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors">
                    {item.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs px-1.5 py-0.5 bg-orange-500/10 text-orange-600 rounded">
                      {item.source}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {item.date}
                    </span>
                  </div>
                </div>
                <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
              </div>
            </a>
          ))}
        </div>
      )}
      
      <div className="mt-3 pt-3 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Ostschweiz / St. Gallen</span>
          {lastUpdated && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {lastUpdated}
            </span>
          )}
        </div>
        {sources.length > 0 && (
          <p className="text-xs text-muted-foreground mt-1 truncate" title={sources.join(', ')}>
            Quellen: {sources.slice(0, 3).join(', ')}{sources.length > 3 ? '...' : ''}
          </p>
        )}
      </div>
    </div>
  );
}
